import { useEffect, useCallback } from 'react';
import { differenceInMilliseconds } from 'date-fns';
import { logger } from '@/utils/logger/logger';
import { useCurrentPath } from '../useCurrentPath/useCurrentPath';
import { useLocale } from '../useLocale/useLocale';
import { getQueryParams } from '@/utils/fp/route/queryParams';
import { determinePathType } from '@/utils/determinePathType';
import { addLocaleToUrl } from '@/utils/locale/addLocaleToUrl';
import { useRouter } from '../useRouter/useRouter';
import { useHasHydrated } from '../useHasHydrated';
import { usePhrasesStore } from '@/store/phrasesStore';
import { useAuthStore } from '@/store/authStore/authStore';
import { useRedirectInfo } from '@/hooks/queries/affiliate/affiliateQueries';
import { useParams } from 'next/navigation';

export const RECENT_DURATION_MS = 500;

const REDIRECT_PATHS = {
    LOGIN: '/login',
    SURVEYS: '/surveys',
    ROOT: '/',
    CONFIRM_PASSWORD: '/login/confirm-password',
};

export const useRedirects = () => {
    const router = useRouter();
    const { uid } = useParams();
    const { pathname, searchParams, asPath, isReady } = useCurrentPath();
    const { locale } = useLocale();
    const hasHydrated = useHasHydrated();

    const { to: toQueryParam, redirect_jwt: redirectJwtQueryParam } = getQueryParams(searchParams)(
        'to',
        'redirect_jwt',
    );

    const {
        locale: { locale: storedLocale },
        setLocale,
    } = usePhrasesStore();

    const {
        isLoggedIn,
        loggedInViaRegistration,
        passwordCheckIsCurrent,
        sessionCheckIsCurrent,
        loginTime,
        logoutTime,
        redirectJwt,
        setRedirectJwt,
        interactionUid,
    } = useAuthStore((state) => ({
        isLoggedIn: state.loggedIn,
        loggedInViaRegistration: state.loggedInViaRegistration,
        loginTime: state.loginTime,
        logoutTime: state.logoutTime,
        passwordCheckIsCurrent: state.passwordCheckIsCurrent(),
        sessionCheckIsCurrent: state.sessionCheckIsCurrent(),
        redirectJwt: state.redirectJwt,
        setRedirectJwt: state.setRedirectJwt,
        interactionUid: state.interactionUid,
    }));

    const isAllowedToRequestAuthorizedData = isReady && isLoggedIn && sessionCheckIsCurrent;
    const { data: redirectInfo } = useRedirectInfo(
        redirectJwt.jwt,
        isAllowedToRequestAuthorizedData,
    );
    const affiliatesRedirectPath = redirectInfo?.redirectUrl ?? '';
    const redirectJwtChecked =
        isAllowedToRequestAuthorizedData && !redirectJwt.jwt && redirectJwt.status === 'checked';

    const { isPasswordProtectedPath, isPrivatePath, isUnavailableToLoggedInUsersPath } =
        determinePathType(pathname);

    const getRedirectToString = () => {
        if (!asPath || asPath.startsWith('/login') || asPath === '/') return '';
        return `?to=${asPath}`;
    };

    const loginDate = getLoginDate(loginTime);
    const logoutDate = getLogoutDate(logoutTime);
    const recentlyLoggedIn = isRecentlyLoggedIn(loginDate);
    const recentlyLoggedOut = isRecentlyLoggedOut(logoutDate);
    const toQueryParamPath = getRedirectToQueryPath(toQueryParam);
    const expiredUserLogin = isLoggedIn && !sessionCheckIsCurrent;
    const invalidAttemptToAccessPrivatePath = !isLoggedIn && isPrivatePath;
    const accessingProtectedPathOnExpiredPassword =
        isLoggedIn && isPasswordProtectedPath && !passwordCheckIsCurrent;
    const loggedInUserAccessingUnavailablePath = isLoggedIn && isUnavailableToLoggedInUsersPath;
    const recentlyLoggedInAndUrlHasToQuery = toQueryParamPath && recentlyLoggedIn;
    const isLocaleMismatch = locale !== storedLocale;
    const hasLocaleMismatchWithStoredLocale = isLocaleMismatch && !!storedLocale;
    const loginRedirectPath = `${REDIRECT_PATHS.LOGIN}${getRedirectToString()}`;
    const confirmPasswordRedirectPath = `${
        REDIRECT_PATHS.LOGIN
    }/confirm-password${getRedirectToString()}`;

    const redirectTo = useCallback(
        (path: string, asPath?: string) => {
            const localeToSet = hasLocaleMismatchWithStoredLocale ? storedLocale : locale;
            if (hasLocaleMismatchWithStoredLocale) {
                setLocale(localeToSet);
            }
            router.redirect(addLocaleToUrl(locale, path, asPath));
        },
        [hasLocaleMismatchWithStoredLocale, storedLocale, locale, setLocale, router],
    );

    const queryParamRedirectCondition = recentlyLoggedInAndUrlHasToQuery && !redirectJwtQueryParam;

    const surveysRedirectCondition =
        loggedInUserAccessingUnavailablePath &&
        !uid &&
        !interactionUid &&
        !recentlyLoggedIn &&
        !redirectJwtQueryParam &&
        !loggedInViaRegistration;

    const conditions = useCallback(
        () => [
            {
                condition: recentlyLoggedOut && !pathname.startsWith('/login'),
                path: REDIRECT_PATHS.LOGIN,
                action: () => {
                    logger.info('Redirecting to login due to recent logout');
                },
            },
            {
                condition: affiliatesRedirectPath,
                path: affiliatesRedirectPath,
            },
            {
                condition: queryParamRedirectCondition,
                path: toQueryParamPath as string,
                action: () => {
                    logger.info('Redirecting due to query param condition');
                },
            },
            {
                condition: invalidAttemptToAccessPrivatePath,
                path: loginRedirectPath,
                action: () => {
                    logger.info('Redirecting due to invalid attempt to access private path');
                },
            },
            {
                condition: accessingProtectedPathOnExpiredPassword,
                path: confirmPasswordRedirectPath,
                action: () => {
                    logger.info('Redirecting due to accessing protected path on expired password');
                },
            },
            {
                condition: surveysRedirectCondition,
                path: REDIRECT_PATHS.SURVEYS,
                action: () => {
                    logger.info('Redirecting due to surveys redirect condition');
                },
            },
            {
                condition: hasLocaleMismatchWithStoredLocale,
                path: asPath,
                action: () => {
                    logger.info('Redirecting due to locale mismatch with stored locale');
                },
            },
        ],
        [
            accessingProtectedPathOnExpiredPassword,
            confirmPasswordRedirectPath,
            invalidAttemptToAccessPrivatePath,
            hasLocaleMismatchWithStoredLocale,
            loginRedirectPath,
            queryParamRedirectCondition,
            recentlyLoggedOut,
            affiliatesRedirectPath,
            asPath,
            pathname,
            surveysRedirectCondition,
            toQueryParamPath,
        ],
    )();

    const shouldNotRedirect = !hasHydrated || expiredUserLogin || !isReady || router.isRedirecting;

    const onRedirectCompletion = useCallback(
        (redirectPath: string) => {
            if (redirectPath === affiliatesRedirectPath || redirectJwtChecked) {
                setRedirectJwt({ jwt: undefined, status: 'redirected' });
            }
        },
        [setRedirectJwt, redirectJwtChecked, affiliatesRedirectPath],
    );

    useEffect(() => {
        if (shouldNotRedirect) return;

        const matchingCondition = conditions.find(({ condition }) => condition);

        if (matchingCondition) {
            if (matchingCondition.path === affiliatesRedirectPath) {
                const asPath = matchingCondition.path.split('?')[0]; // remove query params
                redirectTo(matchingCondition.path, asPath);
                onRedirectCompletion(matchingCondition.path);
            } else {
                redirectTo(matchingCondition.path);
                onRedirectCompletion(matchingCondition.path);
            }
        }
    }, [
        router,
        shouldNotRedirect,
        redirectTo,
        affiliatesRedirectPath,
        queryParamRedirectCondition,
        conditions,
        onRedirectCompletion,
    ]);
};

// Helper functions
const getDate = (time: string | undefined) => (time ? new Date(time).toISOString() : null);
const getLoginDate = (loginTime: string | undefined) => getDate(loginTime);
const getLogoutDate = (logoutTime: string | undefined) => getDate(logoutTime);

const isRecentActivity = (activityDate: string | null) =>
    activityDate
        ? differenceInMilliseconds(new Date(), new Date(activityDate)) < RECENT_DURATION_MS
        : false;
const isRecentlyLoggedIn = (loginDate: string | null) => isRecentActivity(loginDate);
const isRecentlyLoggedOut = (logoutDate: string | null) => isRecentActivity(logoutDate);

const getRedirectToQueryPath = (toQuery: string | string[] | undefined) =>
    Array.isArray(toQuery) ? toQuery.pop() : toQuery;
